import { BdcRtbGuestInfo, BdcRtbRequestStatus } from '@tv3/store/inquiry/inquiry.model'

// TV4-6322: Booking.com onboarding rules require these five reasons, a private free text for
// NOT_COMFORTABLE, and the non-discrimination disclaimer wherever the host approves or declines.
export type BdcRtbRejectionReason =
  | 'DATES_NOT_AVAILABLE'
  | 'POLICY_DONT_FIT_NEEDS'
  | 'OUTSIDE_HOURS_CHECKIN'
  | 'MISSING_INFORMATION'
  | 'NOT_COMFORTABLE'

export const bdcRtbRejectionReasons: { id: BdcRtbRejectionReason; label: string }[] = [
  { id: 'DATES_NOT_AVAILABLE', label: $localize`The dates are not available` },
  { id: 'POLICY_DONT_FIT_NEEDS', label: $localize`The property policies do not fit the guest's needs` },
  { id: 'OUTSIDE_HOURS_CHECKIN', label: $localize`The guest wants to check in outside check-in hours` },
  { id: 'MISSING_INFORMATION', label: $localize`Information about the guest or the stay is missing` },
  { id: 'NOT_COMFORTABLE', label: $localize`I am not comfortable with this request` },
]

export const BDC_RTB_REJECTION_TEXT_MAX = 255

export const bdcRtbDisclaimer = $localize`By accepting or declining this request, you acknowledge that you are doing so in accordance with Booking.com's non-discrimination policy.`

export interface BdcRtbDecisionPayload {
  status: 'APPROVED' | 'REJECTED'
  rejectionReason?: BdcRtbRejectionReason
  rejectionReasonText?: string
}

export const bdcRtbStatusLabels: { [status in BdcRtbRequestStatus]: string } = {
  CREATED: $localize`Waiting for your decision`,
  APPROVED: $localize`Approved`,
  REJECTED: $localize`Declined`,
  BOOKED: $localize`Booked`,
  EXPIRED_PENDING_APPROVAL: $localize`Expired without a decision`,
  EXPIRED_AFTER_APPROVAL: $localize`Expired, the guest did not book`,
  FAILED_TO_PROCESS: $localize`Booking.com could not create the reservation`,
  WITHDRAWN_PENDING_APPROVAL: $localize`Withdrawn by the guest`,
}

export interface BdcRtbGuestSignal {
  label: string
  value: string
  // true: reassuring, false: a warning, null: neutral or unknown.
  positive: boolean | null
}

function yesNo(value: 0 | 1 | null | undefined, positiveWhen: 0 | 1): BdcRtbGuestSignal['positive'] {
  if (value === null || value === undefined) return null
  return value === positiveWhen
}

function yesNoText(value: 0 | 1 | null | undefined): string {
  if (value === null || value === undefined) return $localize`Unknown`
  return value ? $localize`Yes` : $localize`No`
}

export function getBdcRtbGuestSignals(info: BdcRtbGuestInfo): BdcRtbGuestSignal[] {
  if (!info) return []
  return [
    { label: $localize`Email verified`, value: yesNoText(info.emailVerified), positive: yesNo(info.emailVerified, 1) },
    { label: $localize`Phone verified`, value: yesNoText(info.phoneVerified), positive: yesNo(info.phoneVerified, 1) },
    {
      label: $localize`Completed stays`,
      value: info.completedStays ?? $localize`Unknown`,
      positive: null,
    },
    { label: $localize`Previous no-show`, value: yesNoText(info.hasNoShow), positive: yesNo(info.hasNoShow, 0) },
    {
      label: $localize`Past misconduct`,
      value: yesNoText(info.hasPastMisconduct),
      positive: yesNo(info.hasPastMisconduct, 0),
    },
    {
      label: $localize`Stayed with you before`,
      value: yesNoText(info.hasStayWithPartnerBefore),
      positive: yesNo(info.hasStayWithPartnerBefore, 1),
    },
    {
      label: $localize`On Booking.com since`,
      value: info.registeredSince ?? $localize`Unknown`,
      positive: null,
    },
  ]
}
